import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Package, Hash } from 'lucide-react';
import { rentalApi } from '../services/api';
import { RentalStats } from '../types';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities

interface RentalStatsCardProps {
  fairId: number;
}

export default function RentalStatsCard({ fairId }: RentalStatsCardProps) {
  const [stats, setStats] = useState<RentalStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const data = await rentalApi.getRentalStats(fairId);
        setStats(data);
        setError(null);
      } catch (err: unknown) {
        console.error('Failed to load rental stats:', err);
        setError('Failed to load statistics');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [fairId]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error || 'No statistics available'}
      </div>
    );
  }

  // total_rental_revenue is a Decimal string (Issue #890). Treat
  // missing/empty/unparseable as "no revenue yet" rather than rendering $NaN.
  const revenueNum = Number(stats.total_rental_revenue);
  const hasNoRevenue = !Number.isFinite(revenueNum) || revenueNum === 0;

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-6">
      <h3 className="text-lg font-bold text-gray-900">Rental Equipment Statistics</h3>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Revenue */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-700">Total Revenue</span>
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-green-900">
            {formatCurrency(stats.total_rental_revenue)} {/* Issue #905: Decimal.js formatting */}
          </div>
        </div>

        {/* Reservations with Rentals */}
        <div className="bg-gradient-to-br from-teal-50 to-moonbone rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-primary-dark">Reservations</span>
            <TrendingUp className="w-5 h-5 text-secondary" />
          </div>
          <div className="text-2xl font-bold text-ink">
            {stats.total_reservations_with_rentals}
          </div>
          <div className="text-xs text-secondary mt-1">with rental items</div>
        </div>

        {/* Total Items */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-purple-700">Rental Items</span>
            <Package className="w-5 h-5 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-purple-900">
            {stats.total_rental_items}
          </div>
        </div>

        {/* Total Quantity */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-orange-700">Total Quantity</span>
            <Hash className="w-5 h-5 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-orange-900">
            {stats.total_quantity_available}
          </div>
          <div className="text-xs text-orange-600 mt-1">
            {stats.total_quantity_reserved} reserved
          </div>
        </div>
      </div>

      {/* Popular Items */}
      {stats.popular_items && stats.popular_items.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Most Popular Items</h4>
          <div className="space-y-2">
            {stats.popular_items.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-teal-100 text-primary-dark rounded-full font-bold text-sm">
                    {index + 1}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{item.item_name}</div>
                    <div className="text-xs text-gray-500">
                      Reserved {item.times_reserved} times
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-gray-900">
                    {formatCurrency(item.revenue)} {/* Issue #905: Decimal.js formatting */}
                  </div>
                  <div className="text-xs text-gray-500">revenue</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Revenue by Category */}
      {stats.revenue_by_category && stats.revenue_by_category.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Revenue by Category</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {stats.revenue_by_category.map((cat, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-600 capitalize mb-1">{cat.category}</div>
                <div className="text-lg font-bold text-gray-900">
                  {formatCurrency(cat.revenue)} {/* Issue #905: Decimal.js formatting */}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {hasNoRevenue && (
        <div className="text-center py-8 text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No rental revenue yet. Start adding rental items and accepting reservations!</p>
        </div>
      )}
    </div>
  );
}
